import React from 'react';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <img src="/logo.png" alt="TraceMetrics" className="h-10 w-auto" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Política de Privacidad</h1>
        <p className="text-gray-500 mb-10">Última actualización: Marzo 2026</p>

        <div className="prose prose-invert max-w-none space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Responsable del Tratamiento</h2>
            <p>
              <strong>Noakai LLC</strong> ("Noakai", "nosotros") es el responsable del tratamiento 
              de los datos personales recabados a través de la plataforma TraceMetrics. Esta Política 
              de Privacidad explica cómo recopilamos, usamos, almacenamos, protegemos y, en su caso, 
              compartimos su información personal cuando usted utiliza nuestro Servicio.
            </p>
            <p>
              Noakai LLC opera desde los Estados Unidos y presta servicios a nivel global. Al utilizar 
              TraceMetrics, usted consiente el tratamiento de sus datos conforme a esta Política.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Datos que Recopilamos</h2>
            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">2.1. Datos proporcionados por el Usuario</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Datos de registro:</strong> Nombre y dirección de correo electrónico.</li>
              <li><strong>Datos de trading:</strong> Archivos Excel o PDF de operaciones cargados voluntariamente por el Usuario para su análisis.</li>
              <li><strong>Datos de pago:</strong> La información de pago es procesada directamente por Stripe. Noakai no almacena números de tarjeta ni datos financieros sensibles.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">2.2. Datos recopilados automáticamente</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Datos de uso:</strong> Fecha y hora de acceso, páginas visitadas, funcionalidades utilizadas.</li>
              <li><strong>Datos técnicos:</strong> Dirección IP, tipo de navegador, sistema operativo, identificadores de dispositivo.</li>
              <li><strong>Cookies y tecnologías similares:</strong> Utilizamos cookies esenciales para el funcionamiento del Servicio y cookies analíticas para mejorar la experiencia del usuario.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Finalidades del Tratamiento</h2>
            <p>Utilizamos sus datos personales para las siguientes finalidades:</p>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">3.1. Prestación del Servicio</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Crear y gestionar su cuenta de usuario.</li>
              <li>Procesar y analizar los archivos de trading cargados.</li>
              <li>Generar estadísticas, gráficos e informes de rendimiento.</li>
              <li>Gestionar pagos y suscripciones.</li>
              <li>Enviar correos transaccionales (verificación de cuenta, notificaciones de expiración de plan, confirmaciones de pago).</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">3.2. Comunicaciones Comerciales y Marketing</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Enviar ofertas, promociones, novedades y actualizaciones del Servicio.</li>
              <li>Informar sobre nuevas funcionalidades, planes o mejoras de la Plataforma.</li>
              <li>Realizar encuestas de satisfacción y estudios de mercado.</li>
            </ul>
            <p className="mt-2">
              El Usuario puede revocar su consentimiento para recibir comunicaciones comerciales en 
              cualquier momento a través del enlace de cancelación incluido en cada email o contactando 
              a nuestro equipo.
            </p>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">3.3. Mejora y Desarrollo del Servicio</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Analizar el uso del Servicio para mejorar la experiencia del usuario.</li>
              <li>Desarrollar nuevas funcionalidades y optimizar las existentes.</li>
              <li>Realizar análisis estadísticos agregados y anonimizados sobre el uso de la Plataforma.</li>
              <li>Detectar y prevenir fraudes, abusos o actividades no autorizadas.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Base Legal del Tratamiento</h2>
            <p>El tratamiento de sus datos se fundamenta en:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Ejecución del contrato:</strong> El tratamiento es necesario para la prestación del Servicio contratado (registro, análisis de datos, gestión de suscripciones).</li>
              <li><strong>Consentimiento:</strong> Para el envío de comunicaciones comerciales y de marketing. Usted otorga este consentimiento al aceptar estos términos durante el registro y puede revocarlo en cualquier momento.</li>
              <li><strong>Interés legítimo:</strong> Para la mejora del Servicio, la seguridad de la Plataforma y la prevención de fraudes.</li>
              <li><strong>Cumplimiento legal:</strong> Cuando sea necesario para cumplir con obligaciones legales aplicables.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Compartición de Datos</h2>
            <p>No vendemos ni alquilamos sus datos personales. Podemos compartir información con:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Proveedores de servicios:</strong> Stripe (procesamiento de pagos), Resend (envío de correos electrónicos), proveedores de hosting e infraestructura. Estos proveedores solo tienen acceso a los datos necesarios para realizar sus funciones y están obligados contractualmente a proteger su información.</li>
              <li><strong>Autoridades legales:</strong> Cuando sea requerido por ley, orden judicial o proceso legal.</li>
              <li><strong>Transferencias empresariales:</strong> En caso de fusión, adquisición o venta de activos, los datos podrán ser transferidos al sucesor, previo aviso a los usuarios.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Transferencias Internacionales de Datos</h2>
            <p>
              Al ser una empresa con sede en Estados Unidos que opera globalmente, sus datos pueden 
              ser transferidos y procesados en Estados Unidos u otros países donde nuestros proveedores 
              de servicios operan. Nos aseguramos de que dichas transferencias se realicen con las 
              garantías adecuadas:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Cláusulas contractuales estándar aprobadas por autoridades de protección de datos.</li>
              <li>Mecanismos de certificación y marcos de transferencia reconocidos.</li>
              <li>Cumplimiento de los estándares del EU-US Data Privacy Framework cuando sea aplicable.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Conservación de Datos</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Datos de cuenta:</strong> Se conservan mientras la cuenta esté activa y hasta 3 años después de su cancelación para cumplir con obligaciones legales.</li>
              <li><strong>Datos de trading:</strong> Se conservan mientras el Usuario mantenga su cuenta activa. Los archivos procesados pueden ser eliminados periódicamente.</li>
              <li><strong>Datos de pago:</strong> Las referencias de transacciones se conservan conforme a las obligaciones fiscales y contables aplicables (generalmente 5-7 años).</li>
              <li><strong>Datos de uso:</strong> Se conservan de forma agregada y anonimizada sin límite de tiempo para fines estadísticos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Seguridad de los Datos</h2>
            <p>
              Implementamos medidas técnicas y organizativas apropiadas para proteger sus datos, 
              incluyendo:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Cifrado de datos en tránsito (HTTPS/TLS).</li>
              <li>Almacenamiento seguro con acceso restringido.</li>
              <li>Autenticación mediante códigos de verificación por email.</li>
              <li>Monitoreo y revisión periódica de nuestras prácticas de seguridad.</li>
            </ul>
            <p>
              No obstante, ningún sistema es 100% seguro. No podemos garantizar la seguridad 
              absoluta de sus datos, aunque nos esforzamos por protegerlos con las mejores 
              prácticas de la industria.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Derechos del Usuario</h2>
            <p>Dependiendo de su jurisdicción, usted puede tener los siguientes derechos:</p>
            
            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">9.1. Derechos bajo el RGPD (Unión Europea / EEE)</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Acceso:</strong> Solicitar una copia de sus datos personales.</li>
              <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos.</li>
              <li><strong>Supresión:</strong> Solicitar la eliminación de sus datos ("derecho al olvido").</li>
              <li><strong>Limitación:</strong> Restringir el tratamiento de sus datos.</li>
              <li><strong>Portabilidad:</strong> Recibir sus datos en un formato estructurado y legible.</li>
              <li><strong>Oposición:</strong> Oponerse al tratamiento basado en interés legítimo o marketing directo.</li>
              <li><strong>Retirada del consentimiento:</strong> Retirar su consentimiento en cualquier momento sin que ello afecte la licitud del tratamiento previo.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">9.2. Derechos bajo la CCPA/CPRA (California, EE.UU.)</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Derecho a saber qué datos personales recopilamos, usamos y compartimos.</li>
              <li>Derecho a solicitar la eliminación de sus datos personales.</li>
              <li>Derecho a no ser discriminado por ejercer sus derechos de privacidad.</li>
              <li>Derecho a optar por la no venta de sus datos personales (no vendemos datos).</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">9.3. Derechos bajo la CAN-SPAM Act (EE.UU.)</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Derecho a cancelar la suscripción a comunicaciones comerciales en cualquier momento.</li>
              <li>Procesaremos su solicitud de baja en un plazo máximo de 10 días hábiles.</li>
            </ul>

            <p className="mt-4">
              Para ejercer cualquiera de estos derechos, puede contactarnos a través del 
              correo electrónico indicado en la sección de Contacto.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Menores de Edad</h2>
            <p>
              El Servicio no está dirigido a menores de 18 años. No recopilamos conscientemente 
              datos de menores. Si tenemos conocimiento de que un menor nos ha proporcionado datos 
              personales, los eliminaremos de inmediato.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Cookies</h2>
            <p>
              Utilizamos cookies y tecnologías similares estrictamente necesarias para el 
              funcionamiento del Servicio (autenticación, preferencias de sesión). No utilizamos 
              cookies de seguimiento de terceros con fines publicitarios.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Modificaciones</h2>
            <p>
              Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos 
              de cualquier cambio material por correo electrónico o mediante un aviso destacado 
              en la Plataforma. La fecha de "Última actualización" al inicio de este documento 
              indicará cuándo se realizaron los últimos cambios.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Contacto</h2>
            <p>
              Para cualquier consulta o solicitud relacionada con esta Política de Privacidad 
              o el ejercicio de sus derechos, puede contactarnos a través del formulario de 
              contacto disponible en nuestra página web (<strong>tracemetrics.online</strong>), 
              en la sección "Contacto".
            </p>
            <ul className="list-none space-y-1">
              <li><strong>Responsable:</strong> Noakai LLC</li>
              <li><strong>Plataforma:</strong> TraceMetrics</li>
              <li><strong>Canal de contacto:</strong> Formulario web en tracemetrics.online</li>
            </ul>
            <p className="mt-4">
              Si reside en la Unión Europea y considera que el tratamiento de sus datos no 
              cumple con el RGPD, tiene derecho a presentar una reclamación ante la autoridad 
              de protección de datos de su país.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-gray-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} TraceMetrics · Noakai LLC. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
